import isEmpty from 'lodash/isEmpty';
import { CheckIcon } from '@heroicons/react/outline';
import { useDispatch } from 'react-redux';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toIntlDateTimeFormat } from '@zedoc/date';
import Button from '../../../components/Button';
import ConfirmAction from '../../../components/dialogs/ConfirmAction';
import { notifySuccess, notifyError } from '../../../utils/notify';
import { apiZedocPatientResponsesReview } from '../../../common/api/zedoc';
import { callMethod } from '../../../common/utilsClient/ddp/actions';

const ReviewHistory = ({ activityId, milestone, activity }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const dispatch = useDispatch();

  const updateResponseReview = (shouldSendNotification) => {
    return dispatch(
      callMethod(apiZedocPatientResponsesReview, {
        activityId: activityId,
        shouldSendNotification,
      }),
    );
  };

  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const handleOnDialogOpen = () => setIsDialogVisible(true);
  const handleOnDialogClose = () => setIsDialogVisible(false);

  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [
    isSubmittingReviewAndSendNotification,
    setIsSubmittingReviewAndSendNotification,
  ] = useState(false);

  const reviews =
    activity &&
    activity.reviewHistory?.map((historyItem) => ({
      createdBy: historyItem.clinician,
      createdAt: new Date(historyItem.date),
    }));

  const handleOnReview = () => {
    setIsSubmittingReview(true);
    updateResponseReview(false)
      .then(() => {
        setIsSubmittingReview(false);
        handleOnDialogClose();
      })
      .catch((error) => {
        setIsSubmittingReview(false);
        handleOnDialogClose();
        notifyError()(error.message);
      });
  };
  const handleOnReviewAndSendNotification = () => {
    setIsSubmittingReviewAndSendNotification(true);
    updateResponseReview(true)
      .then(() => {
        setIsSubmittingReviewAndSendNotification(false);
        handleOnDialogClose();
        notifySuccess(t('notificationSent'))();
      })
      .catch((error) => {
        setIsSubmittingReviewAndSendNotification(false);
        handleOnDialogClose();
        notifyError()(
          error.message ? error.message.split('[')[0] : t('notificationError'),
        );
      });
  };

  return (
    <>
      <div className="stack-4">
        <Button type="primary" onClick={handleOnDialogOpen}>
          Mark milestone as reviewed
        </Button>
        {!isEmpty(reviews) && (
          <div className="stack-1">
            <p className="font-medium">{t('reviewHistory')}</p>
            <ul>
              {reviews.map((review) => (
                <li className="cluster-1 items-center">
                  <CheckIcon className="w-5 h-5" />
                  <span>
                    {review.createdBy} on{' '}
                    {toIntlDateTimeFormat(language, review.createdAt, {
                      dateStyle: 'long',
                    })}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <ConfirmAction
        open={isDialogVisible}
        title="Mark milestone as reviewed"
        message="Are you sure you want to mark this response as reviewed?"
        onCancel={handleOnDialogClose}
        isCancelDisabled={
          isSubmittingReview || isSubmittingReviewAndSendNotification
        }
        footer={
          <>
            <Button
              onClick={handleOnReview}
              loading={isSubmittingReview}
              disabled={isSubmittingReviewAndSendNotification}
            >
              Just mark as reviewed
            </Button>
            <Button
              type="primary"
              onClick={handleOnReviewAndSendNotification}
              loading={isSubmittingReviewAndSendNotification}
              disabled={isSubmittingReview}
            >
              Mark and send notification
            </Button>
          </>
        }
      />
    </>
  );
};

export default ReviewHistory;
